import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button, FormGroup } from 'reactstrap';

import { EmbeddedForm, formShape } from '@techchange/components';
import {
	DangerAlert,
	SuccessAlert,
	useLanguageContext,
	appPropTypes,
	API_BASE_URL,
} from '@techchange/components';
import { messages } from '../../../../i18n';

import PollResults from './components/PollResults';

const axiosClient = axios.create({
	baseURL: API_BASE_URL.FORM_ENTRIES,
	withCredentials: true,
});

const propTypes = {

	/**
	 * Flag indicating if the content title should be visible.
	 */
	showTitle: PropTypes.bool,

	/**
	 * The form for the poll.
	 */
	form: formShape.isRequired,

	/**
	 * Flag indicating if the current user is an administrator.
	 */
	isAdmin: PropTypes.bool,

	/**
	 * The current user's id.
	 */
	userId: PropTypes.string,

	/**
	 * The users related to the scope of this content,
	 * (i.e. all enrolled users in a session).
	 *
	 * NOTE: Map from user ID -> user data.
	 */
	users: appPropTypes.allUsers.isRequired,

	/**
	 * The TechChange socket instance to handle real-time events.
	 */
	socket: appPropTypes.socket.isRequired,

	/**
	 * Callback function to handle clicking on a user.
	 */
	onUserClick: PropTypes.func.isRequired,

};

const ContentDisplay = (props) => {

	const {
		showTitle,
		form,
		isAdmin,
		userId,
		users,
		socket,
		onUserClick,
	} = props;

	const { chooseContentFromLanguage } = useLanguageContext();

	// Keep track of all the form entries for the poll form
	const [formEntries, setFormEntries] = useState(null);

	// Keep track of the current user's form entry for the poll form
	const [myFormEntry, setMyFormEntry] = useState(null);

	const [isEditing, setIsEditing] = useState(false);

	const [showErrorMsg, setShowErrorMsg] = useState(false);
	const [showSuccessMsg, setShowSuccessMsg] = useState(false);

	const refErrorAlert = useRef();
	const refSuccessAlert = useRef();

	const formID = form?.id;
	const scope = form?.scope;
	const scopeID = form?.scope_id;

	const handleSave = (data) => {

		data.user_id = userId;

		let promise;

		// Check if we need to update an existing form entry for the current user
		if (data.id) {

			promise = axiosClient.patch(`/${data.id}`, data);

		// Otherwise, create a new one!
		} else {

			promise = axiosClient.post('/', data);

		}

		// Reset the API success/error messages
		setShowErrorMsg(false);
		setShowSuccessMsg(false);

		promise.then((response) => {

			if (response && response.data) {

				setIsEditing(false);
				setShowSuccessMsg(true);

				const updatedEntry = Object.assign({}, response.data);

				setMyFormEntry(updatedEntry);

				setFormEntries((prev) => {

					const updatedFormEntries = [...prev];

					// Find the existing form entry (if it exists)
					const index = updatedFormEntries.findIndex(
						(entry) => entry.id === updatedEntry.id);

					// Check if an existing form entry was updated
					if (index >= 0) {

						updatedFormEntries[index] = Object.assign({}, updatedEntry);

					// Otherwise, a new form entry was created
					} else {

						updatedFormEntries.push(Object.assign({}, updatedEntry));

					}

					return updatedFormEntries;

				});

			}

		}).catch((error) => {

			console.error(error);

			setShowErrorMsg(true);

		});

	};

	const handleToggleView = (event) => {

		if (event) {
			event.preventDefault();
		}

		setIsEditing((prev) => !prev);

		setShowErrorMsg(false);
		setShowSuccessMsg(false);

	};

	// Fetch all the form entries for this poll form
	useEffect(() => {

		// Ignore this if we already have the form entries
		if (formEntries) {
			return;
		}

		// Fetch the form entries for the poll form
		axiosClient.get('/', {
			params: {
				form_id: formID,
				scope,
				scope_id: scopeID,
			}
		}).then((response) => {

			if (response && response.data) {

				const tempFormEntries = [...response.data];

				// Find the current user's form entry (if it exists)
				const tempMyFormEntry = tempFormEntries.find((entry) => entry.user_id === userId);

				setMyFormEntry(tempMyFormEntry);

				setIsEditing(!tempMyFormEntry);

				setFormEntries(tempFormEntries);

			}

		});

	}, [formEntries, userId, formID, scope, scopeID]);

	// Listen for real-time updates to the form entries for this poll form
	useEffect(() => {

		if (!scope || !scopeID) {
			return () => {};
		}

		const onRealTimeUpdate = (updatedEntry) => {

			// Double check to make sure the updated entry is for this poll form
			if (updatedEntry.form_id === formID) {

				setFormEntries((prev) => {

					if (!prev) {
						return prev;
					}

					const updatedFormEntries = [...prev];

					// Find the existing form entry (if it exists)
					const index = updatedFormEntries.findIndex(
						(entry) => entry.id === updatedEntry.id);

					// Check if an existing form entry was updated
					if (index >= 0) {

						updatedFormEntries[index] = Object.assign({}, updatedEntry);

					// Otherwise, a new form entry was created
					} else {

						updatedFormEntries.push(Object.assign({}, updatedEntry));

					}

					return updatedFormEntries;

				});

				// The current user voted from another tab or device: show
				// their new answer here too, not the one loaded with the page
				if (userId && updatedEntry.user_id === userId) {

					setMyFormEntry(Object.assign({}, updatedEntry));
					setIsEditing(false);

				}

			}

		};

		// Listen for real-time updates of form entries
		if (socket) {
			socket.on('update:form-entry', onRealTimeUpdate);
		}

		// Clean up the real-time listener for updates of form entries
		return () => {

			if (socket) {
				socket.removeListener('update:form-entry', onRealTimeUpdate);
			}

		};

	}, [formID, scope, scopeID, socket, userId]);

	// Auto-scroll to the success/error message (if needed)
	useEffect(() => {

		if (showErrorMsg && refErrorAlert?.current) {
			refErrorAlert.current.scrollIntoView({ behavior: 'smooth' });
		}

		if (showSuccessMsg && refSuccessAlert?.current) {
			refSuccessAlert.current.scrollIntoView({ behavior: 'smooth' });
		}

	}, [showErrorMsg, showSuccessMsg]);

	// Check if we are still loading the associated form entries
	if (!formEntries) {
		return <h1 className="p-3"><i className="fas fa-circle-notch fa-spin" /></h1>;
	}

	// Check if the learner can view the aggregated results for the poll
	const canViewResults = isEditing && isAdmin;

	// Check if the learner can edit a previous form entry
	const canEditEntry = !isEditing && form.allow_editing_submissions;

	// Get the translated content for the form
	const formContent = chooseContentFromLanguage(form.form_contents);

	return (
		<div className="tc-content-builder-form-entry">
			<FormGroup>
				{showTitle && formContent?.name &&
					<h5 className="tc-content-builder-form-title">
						{formContent.name}
					</h5>
				}
				{formContent?.description &&
					<span className="tc-content-builder-form-description">
						{formContent.description}
					</span>
				}
			</FormGroup>
			{showErrorMsg &&
				<div ref={refErrorAlert}>
					<DangerAlert>
						<FormattedMessage {...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_FORM_ENTRY_MSG_TEXT_SUBMIT_ERROR} />
					</DangerAlert>
				</div>
			}
			{showSuccessMsg &&
				<div ref={refSuccessAlert}>
					<SuccessAlert>
						<FormattedMessage {...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_FORM_ENTRY_MSG_TEXT_SUBMIT_SUCCESS} />
					</SuccessAlert>
				</div>
			}
			{(myFormEntry || canEditEntry || canViewResults) &&
				<div className="tc-content-builder-form-entry-metadata">
					{myFormEntry &&
						<span className="timestamp">
							<FormattedMessage
								{...messages.TC_COMPONENTS_GENERAL_TEXT_SUBMITTED_DATE}
								values={{
									date: moment(myFormEntry.updated_at).format('MMMM D, YYYY h:mm A'),
								}} />
						</span>
					}
					<div className="form-entry-action-links">
						{canEditEntry &&
							<Button
								color="link"
								onClick={handleToggleView}>
								<FormattedMessage
									{...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_FORM_BUTTON_TEXT_CHANGE_FORM_ENTRY} />
							</Button>
						}
						{canEditEntry && canViewResults &&
							<span>{'|'}</span>
						}
						{canViewResults &&
							<Button
								color="link"
								onClick={handleToggleView}>
								<FormattedMessage {...messages.TC_COMPONENTS_RESOURCE_CONTENT_BUILDER_POLL_ENTRY_VIEW_RESULTS} />
							</Button>
						}
					</div>
				</div>
			}
			{isEditing &&
				<EmbeddedForm
					form={form}
					formEntry={myFormEntry}
					isEditing
					onCancel={myFormEntry ? handleToggleView : null}
					onSave={handleSave} />
			}
			{!isEditing &&
				<PollResults
					form={form}
					formEntries={formEntries}
					userId={userId}
					users={users}
					onUserClick={onUserClick} />
			}
		</div>
	);

};

ContentDisplay.propTypes = propTypes;

export default ContentDisplay;
